import React, { useEffect } from 'react';

const StatePattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'State Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the State Pattern - allow objects to alter behavior when internal state changes. Learn with vending machine and media player examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the State Pattern - allow objects to alter behavior when internal state changes. Learn with vending machine and media player examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'state pattern, design patterns, behavioral patterns, state machine, finite state machine, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'state pattern, design patterns, behavioral patterns, state machine, finite state machine, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>State Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Change behavior based on internal state
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The State pattern allows an object to alter its behavior when its internal state changes. 
            The object will appear to change its class. It's useful for implementing finite state 
            machines and managing complex state-dependent behavior.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You have an object that behaves differently depending on its current state, and the 
            number of states is large. Managing this with if-else statements or switch cases 
            becomes unwieldy and error-prone.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Media Player</h2>
          <div style={styles.codeBlock}>
{`// State interface
interface PlayerState {
    void play(MediaPlayer player);
    void pause(MediaPlayer player);
    void stop(MediaPlayer player);
    String getStatus();
}

// Context class
class MediaPlayer {
    private PlayerState currentState;
    private String trackName;
    
    public MediaPlayer(String trackName) {
        this.trackName = trackName;
        this.currentState = new StoppedState();
    }
    
    public void setState(PlayerState state) {
        this.currentState = state;
    }
    
    public void play() {
        currentState.play(this);
    }
    
    public void pause() {
        currentState.pause(this);
    }
    
    public void stop() {
        currentState.stop(this);
    }
    
    public String getStatus() {
        return currentState.getStatus() + " - " + trackName;
    }
    
    public String getTrackName() {
        return trackName;
    }
}

// Concrete states
class StoppedState implements PlayerState {
    @Override
    public void play(MediaPlayer player) {
        System.out.println("Starting playback of: " + player.getTrackName());
        player.setState(new PlayingState());
    }
    
    @Override
    public void pause(MediaPlayer player) {
        System.out.println("Cannot pause. Player is stopped.");
    }
    
    @Override
    public void stop(MediaPlayer player) {
        System.out.println("Player is already stopped.");
    }
    
    @Override
    public String getStatus() {
        return "Stopped";
    }
}

class PlayingState implements PlayerState {
    @Override
    public void play(MediaPlayer player) {
        System.out.println("Already playing: " + player.getTrackName());
    }
    
    @Override
    public void pause(MediaPlayer player) {
        System.out.println("Pausing: " + player.getTrackName());
        player.setState(new PausedState());
    }
    
    @Override
    public void stop(MediaPlayer player) {
        System.out.println("Stopping: " + player.getTrackName());
        player.setState(new StoppedState());
    }
    
    @Override
    public String getStatus() {
        return "Playing";
    }
}

class PausedState implements PlayerState {
    @Override
    public void play(MediaPlayer player) {
        System.out.println("Resuming: " + player.getTrackName());
        player.setState(new PlayingState());
    }
    
    @Override
    public void pause(MediaPlayer player) {
        System.out.println("Already paused: " + player.getTrackName());
    }
    
    @Override
    public void stop(MediaPlayer player) {
        System.out.println("Stopping from pause: " + player.getTrackName());
        player.setState(new StoppedState());
    }
    
    @Override
    public String getStatus() {
        return "Paused";
    }
}

// Usage
public class StatePatternDemo {
    public static void main(String[] args) {
        MediaPlayer player = new MediaPlayer("Bohemian Rhapsody");
        
        System.out.println("Initial status: " + player.getStatus());
        
        player.play();    // Start playing
        System.out.println("Status: " + player.getStatus());
        
        player.pause();   // Pause
        System.out.println("Status: " + player.getStatus());
        
        player.play();    // Resume
        System.out.println("Status: " + player.getStatus());
        
        player.stop();    // Stop
        System.out.println("Status: " + player.getStatus());
        
        player.pause();   // Try to pause when stopped
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Clean Code:</strong> Eliminates complex conditional statements</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Each state handles its own behavior</li>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Easy to add new states</li>
            <li style={styles.listItem}><strong>State Transitions:</strong> Clear and controlled state transitions</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When object behavior depends on its state</li>
            <li style={styles.listItem}>When you have large conditional statements based on state</li>
            <li style={styles.listItem}>When you need to implement finite state machines</li>
            <li style={styles.listItem}>When state transitions are complex</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The State pattern is perfect for managing complex state-dependent behavior. It makes 
            code more maintainable by organizing state-specific behavior into separate classes 
            and making state transitions explicit and controlled.
          </p>
        </section>
      </main>
    </article>
  );
};

export default StatePattern;
